import { T } from '@tolgee/react';
import { Modal } from 'antd';
import { useRouter } from 'next/router';
import { FunctionComponent } from 'react';
import styled from 'styled-components';

import { CuriConfused } from '../../../assets/icons/CuriConfused';
import { LogoSvg } from '../../../assets/icons/LogoSvg';
import theme from '../../../assets/theme';
import { Button } from '../../_atoms/Button';
import { IconWrapper } from '../../_atoms/IconWrapper';

interface DailyLimitModalProps {
  open: boolean;
  onClose: () => void;
}

export const DailyLimitModal: FunctionComponent<DailyLimitModalProps> = ({
  open,
  onClose,
}) => {
  const router = useRouter();

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      destroyOnClose
      width={520}
      closable={true}
      maskClosable={true}
      bodyStyle={{
        padding: 0,
        backgroundColor: theme.colors.background,
        borderRadius: '10px',
      }}
    >
      <Container>
        <IconWrapper color={theme.colors.orange} height="36px" width="130px">
          <LogoSvg />
        </IconWrapper>

        <Title>
          <T keyName="daily-limit-modal.title" defaultValue="Daily AI Limit Reached" />
        </Title>

        <InfoCard>
          <IconWrapper height="72px" width="72px">
            <CuriConfused />
          </IconWrapper>
          <Description>
            <T
              keyName="daily-limit-modal.description"
              defaultValue="You've used all your AI generations for today. You can still create lessons from blank. Your limit resets tomorrow - come back then to create more!"
            />
          </Description>
        </InfoCard>

        <UpgradeHint>
          <T
            keyName="daily-limit-modal.upgrade-hint"
            defaultValue="Users on a premium or school plan get unlimited AI generations."
          />
        </UpgradeHint>

        <ButtonContainer>
          <Button type="ghost" onClick={onClose}>
            <T keyName="daily-limit-modal.ok" defaultValue="Got it" />
          </Button>
          <Button
            type="primary"
            // Was a <Link><a><Button> sandwich: `onClose` + `destroyOnClose`
            // unmounted the anchor during the same click, so the browser never
            // followed the href and the CTA did nothing. Route explicitly.
            onClick={() => {
              onClose();
              router.push('/pricing');
            }}
            styles={{
              backgroundColor: theme.colors.orange,
              borderColor: theme.colors.orange,
            }}
          >
            <T
              keyName="daily-limit-modal.see-plans"
              defaultValue="See plans and pricing"
            />
          </Button>
        </ButtonContainer>
      </Container>
    </Modal>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 32px 40px;
  gap: 16px;
  background-color: ${theme.colors.background};
  border-radius: 10px;

  @media (max-width: 480px) {
    padding: 24px 20px;
  }
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${theme.colors.black};
  margin: 0;

  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

const InfoCard = styled.div`
  width: 100%;
  background-color: ${theme.colors.white};
  border-radius: 10px;
  padding: 16px 20px;
  box-shadow: 0px 2.6px 5.6px rgba(0, 0, 0, 0.08), 0px 0.9px 1.3px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
`;

const Description = styled.p`
  font-size: 15px;
  color: ${theme.colors.greyDark};
  margin: 0;
  line-height: 1.6;
  text-align: left;
`;

const UpgradeHint = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.orange};
  margin: 0;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 4px;

  @media (max-width: 480px) {
    flex-direction: column;
    width: 100%;

    & > * {
      width: 100%;
    }
  }
`;
